import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Checkbox,
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@clouway/ui'

const DIGITAL_TYPES = ['door', 'ignition', 'panicButton', 'externalPower'] as const

// Form shape — decoupled from the wire DigitalPortConfig (port is a string here
// because it comes from a Select; the tab maps it to a number).
const buildSchema = (requiredMessage: string) =>
  z.object({
    port: z.string().min(1, requiredMessage),
    type: z.string().min(1, requiredMessage),
    working: z.boolean(),
    inverse: z.boolean(),
  })

export type DigitalInputFormValues = z.infer<ReturnType<typeof buildSchema>>

export function DigitalInputEditForm({
  availablePorts,
  initialValues,
  onSubmit,
  onCancel,
}: {
  availablePorts: string[]
  initialValues?: DigitalInputFormValues
  onSubmit: SubmitHandler<DigitalInputFormValues>
  onCancel: () => void
}) {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const schema = useMemo(() => buildSchema(t('digitalInputForm.required')), [t])
  const { control, handleSubmit, formState } = useForm<DigitalInputFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? { port: '', type: '', working: true, inverse: false },
  })

  return (
    <form className="flex flex-col gap-4 rounded-md border p-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="port"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('digitalInputForm.port')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('digitalInputForm.port')}>
                <SelectValue placeholder={tCommon('ui.label.select')} />
              </SelectTrigger>
              <SelectContent>
                {availablePorts.map((port) => (
                  <SelectItem key={port} value={port}>
                    {port}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="type"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('digitalInputForm.type')}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={t('digitalInputForm.type')}>
                <SelectValue placeholder={tCommon('ui.label.select')} />
              </SelectTrigger>
              <SelectContent>
                {DIGITAL_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`trackingObjects.config.digitalInputTypes.${type}` as 'trackingObjects.config.digitalInputTypes.door')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />

      <Controller
        control={control}
        name="working"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />
            {t('digitalInputForm.active')}
          </label>
        )}
      />

      <Controller
        control={control}
        name="inverse"
        render={({ field }) => (
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />
            {t('digitalInputForm.reversed')}
          </label>
        )}
      />

      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('digitalInputForm.cancel')}
        </Button>
        <Button type="submit" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? t('digitalInputForm.submitting') : t('digitalInputForm.submit')}
        </Button>
      </div>
    </form>
  )
}
